import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../../api/axios';
import { useLoading } from '../../../../components/LoadingProvider';
import useAuth from '../../../../hooks/useAuth';
import { showSuccess, showError } from '../../../../utils/toast';
import { getIndStates } from '../../../../utils/common';

export default function DoctorAppointmentSpeechTherapyPrescriptionPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    document.title = `Varosa - Doctor Appointment Speech Therapy Prescription No ${id}`;
    const { setIsLoading } = useLoading();
    const { userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const [ appointmentPrescriptionData, setAppointmentPrescriptionData ] = useState(null);
    const [ oldAppointments, setOldAppointments ] = useState([]);
    const prefixOfName = [ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ];
    const [ accordionStatus, setAccordionStatus ] = useState({
        one: true,
    });
    const formPatientRef = useRef(null);
    const formRef = useRef(null);
    const formMedicineRef = useRef(null);
    const indStates = getIndStates();
    const [ prescriptionFormData, setPrescriptionFormData ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_line2: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        provisional_diagnosis: '',
        present_concerns: '',
        assessment_findings: '',
        advice_home_program: '',
        recommendations: '',
        frequency_of_therapy: '',
        review_date: '',
        therapy_plan: [],
        is_completed: 'Yes',
        prescription_type: 'speech_therapy',
    });
    const [ prescriptionFormError, setPrescriptionFormError ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        provisional_diagnosis: '',
        present_concerns: '',
        assessment_findings: '',
        advice_home_program: '',
        recommendations: '',
        frequency_of_therapy: '',
        review_date: '',
        therapy_plan: '',
        remarks: '',
        is_completed: '',
    });
    useEffect(() => {
        fetchAppointmentPrescription();
    }, []);
    const fetchAppointmentPrescription = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/speech-therapy-prescription`);
            if (response?.data?.result === 'success') {
                setAppointmentPrescriptionData(response?.data?.data);
                setOldAppointments(response?.data?.data.old_appointments);
                let found_state = response?.data?.data?.patient?.state || '';
                if(found_state.length > 2){
                    let ind_state = indStates.find(s => {
                        return found_state.replaceAll(' ', '').toLocaleLowerCase() === s.name.replaceAll(' ', '').toLocaleLowerCase();
                    });
                    if(ind_state){
                        found_state = ind_state.key;
                    }
                }
                setPrescriptionFormData({
                    patient_name: response?.data?.data?.patient?.name || '',
                    patient_name_prefix: response?.data?.data?.patient?.name_prefix || '',
                    patient_dob: response?.data?.data?.patient?.dob || '',
                    patient_gender: response?.data?.data?.patient?.gender || '',
                    patient_address_line1: response?.data?.data?.patient?.address_line1 || '',
                    patient_address_line2: response?.data?.data?.patient?.address_line2 || '',
                    patient_address_district: response?.data?.data?.patient?.district || '',
                    patient_address_state: found_state,
                    patient_address_pincode: response?.data?.data?.patient?.pincode || '',
                    patient_address_country: response?.data?.data?.patient?.country || '',
                    provisional_diagnosis: response?.data?.data?.appointment_prescription?.provisional_diagnosis || '',
                    present_concerns: response?.data?.data?.appointment_prescription?.present_concerns || '',
                    assessment_findings: response?.data?.data?.appointment_prescription?.assessment_findings || '',
                    advice_home_program: response?.data?.data?.appointment_prescription?.advice_home_program || '',
                    recommendations: response?.data?.data?.appointment_prescription?.recommendations || '',
                    frequency_of_therapy: response?.data?.data?.appointment_prescription?.frequency_of_therapy || '',
                    review_date: response?.data?.data?.appointment_prescription?.review_date || '',
                    therapy_plan: response?.data?.data?.appointment_prescription?.therapy_plan || [],
                    remarks: response?.data?.data?.appointment_prescription?.remarks || '',
                    is_completed: 'Yes',
                    prescription_type: 'speech_therapy',
                });
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const addPlanFormData = () => {
        setPrescriptionFormData({ ...prescriptionFormData, therapy_plan: [...prescriptionFormData?.therapy_plan, {'id': '','instruction': ''}] });
    }
    const removePlanFormData = (index) => {
        setPrescriptionFormData({ ...prescriptionFormData, therapy_plan: [...prescriptionFormData?.therapy_plan.filter((item, i) => i !== index)] });
    }
    const handleAppointmentDateChange = async (e) => {
        const { name, value } = e.target;
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/old-prescription?type=speech_therapy&search_date=`+value);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setOldAppointments(response?.data?.data.old_appointments);
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const handlePrescription = async () => {
        try {
            const resetPrescriptionFormError = {
                patient_name: '',
                patient_name_prefix: '',
                patient_dob: '',
                patient_gender: '',
                patient_address_line1: '',
                patient_address_line2: '',
                patient_address_district: '',
                patient_address_state: '',
                patient_address_pincode: '',
                provisional_diagnosis: '',
                present_concerns: '',
                assessment_findings: '',
                advice_home_program: '',
                recommendations: '',
                frequency_of_therapy: '',
                review_date: '',
                therapy_plan: '',
                is_completed: '',
            };
            setPrescriptionFormError(resetPrescriptionFormError);
            if (!prescriptionFormData?.patient_name) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name: 'Patient Name is required' });
                return;
            }
            if (!prescriptionFormData?.patient_name_prefix) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name_prefix: 'Patient Name Prefix is required' });
                return;
            }
            if (!prescriptionFormData?.patient_dob) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_dob: 'Patient DOB is required' });
                return;
            }
            if (!prescriptionFormData?.patient_gender) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_gender: 'Patient Gender is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_line1) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_line1: 'Patient Address line 1 is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_district) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_district: 'Patient District is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_state) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_state: 'Patient State is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_pincode) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_pincode: 'Patient Pincode is required' });
                return;
            }
            if (!prescriptionFormData?.provisional_diagnosis) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, provisional_diagnosis: 'Chief complaints is required' });
                return;
            }
            if (!prescriptionFormData?.present_concerns) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, present_concerns: 'History points is required' });
                return;
            }
            if (!prescriptionFormData?.assessment_findings) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, assessment_findings: 'Assessment Findings is required' });
                return;
            }
            if (!prescriptionFormData?.advice_home_program) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, advice_home_program: 'Advice/Home Program is required' });
                return;
            }
            if (!prescriptionFormData?.recommendations) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, recommendations: 'Suggested investigations is required' });
                return;
            }
            if (!prescriptionFormData?.frequency_of_therapy) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, frequency_of_therapy: 'Frequency of Therapy is required' });
                return;
            }
            if (!prescriptionFormData?.review_date) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, review_date: 'Review Date is required' });
                return;
            }
            if (!prescriptionFormData?.therapy_plan?.length) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, therapy_plan: 'Therapy Plan is required' });
                return;
            }
            if (!prescriptionFormData?.is_completed) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, is_completed: 'Appointment Completed is required' });
                return;
            }
            setIsLoading(true);
            const response = await api.post(`/${userRole}/appointment/${id}/speech-therapy-prescription`, prescriptionFormData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                fetchAppointmentPrescription();  
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <>
            <div className="container py-5 login-container appointment-detail">
                <div className="row g-3">
                    <div className="col-8">
                        <h2 className="mb-4">Appointment Speech Therapy Prescription No {id}</h2>
                    </div>
                    <div className="col-4 text-end">
                        <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                    </div>
                    <hr />
                </div>
                <div className="row g-3">
                    <div className="col-md-12">
                        <div className="accordion">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingOne" 
                                    onClick={() => setAccordionStatus({ ...accordionStatus, one: !accordionStatus.one })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.one ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne" aria-expanded="true" aria-controls="panelsStayOpen-collapseOne">
                                        Previous Appointments
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseOne" className={`accordion-collapse collapse ${accordionStatus.one ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingOne">
                                    <div className="accordion-body">
                                        <div className="row">
                                            <div className="col-12">
                                                <label className="form-label">Select Appointment Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control py-3 border-primary"
                                                    name="appointment_date"
                                                    onChange={handleAppointmentDateChange}
                                                    max={new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                            <hr className="mt-4 mb-3" />
                                            <div className="col-12">
                                                {oldAppointments.length > 0 ?
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col" style={{ width: '10%' }}>IDs</th>
                                                            <th scope="col" style={{ width: '30%' }}>Appointment Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Patient Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {oldAppointments?.map((item, index) => (<tr key={index}>
                                                            <th scope="row" style={{ width: '10%' }}>{item.appointment.id}</th>
                                                            <td style={{ width: '30%' }}>
                                                                Date & Time: <b>{item.appointment.appointment_date_time}</b><br />
                                                                Appointment Completed: <b>{item.appointment.is_completed}</b>
                                                            </td>
                                                            <td style={{ width: '30%' }}>
                                                                Name: <b>{item.appointment.patient_name}</b><br />
                                                                Age: <b>{item.appointment.patient_age} Yrs</b><br />
                                                                Gender: <b>{item.appointment.patient_gender.toUpperCase()}</b><br />
                                                                Address: <b>{item.appointment.patient_address}</b>
                                                            </td>
                                                            <td style={{ width: '30%' }}>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-primary btn-sm mt-1 mb-1 me-1"
                                                                    onClick={(e) => {
                                                                        let found_state = item.patient.state || '';
                                                                        if(found_state.length > 2){
                                                                            let ind_state = indStates.find(s => {
                                                                                return found_state.replaceAll(' ', '').toLocaleLowerCase() === s.name.replaceAll(' ', '').toLocaleLowerCase();
                                                                            });
                                                                            if(ind_state){
                                                                                found_state = ind_state.key;
                                                                            }
                                                                        }
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            patient_name_prefix: item.patient.name_prefix,
                                                                            patient_name: item.patient.name,
                                                                            patient_dob: item.patient.dob,
                                                                            patient_gender: item.patient.gender,
                                                                            patient_address_line1: item.patient.address_line1,
                                                                            patient_address_line2: item.patient.address_line2,
                                                                            patient_address_district: item.patient.district,
                                                                            patient_address_state: found_state,
                                                                            patient_address_pincode: item.patient.pincode,
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            patient_name_prefix: '',
                                                                            patient_name: '',
                                                                            patient_dob: '',
                                                                            patient_gender: '',
                                                                            patient_address_line1: '',
                                                                            patient_address_district: '',
                                                                            patient_address_state: '',
                                                                            patient_address_pincode: '',
                                                                        });
                                                                        formPatientRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Patient Data</button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-secondary btn-sm mt-1 mb-1 me-1" 
                                                                    onClick={(e) => {
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            provisional_diagnosis: item.appointment_prescription.provisional_diagnosis,
                                                                            present_concerns: item.appointment_prescription.present_concerns,
                                                                            assessment_findings: item.appointment_prescription.assessment_findings,
                                                                            advice_home_program: item.appointment_prescription.advice_home_program,
                                                                            recommendations: item.appointment_prescription.recommendations,
                                                                            frequency_of_therapy: item.appointment_prescription.frequency_of_therapy,
                                                                            review_date: item.appointment_prescription.review_date,
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            provisional_diagnosis: '',
                                                                            present_concerns: '',
                                                                            assessment_findings: '',
                                                                            advice_home_program: '',
                                                                            recommendations: '',
                                                                            frequency_of_therapy: '',
                                                                            review_date: '',
                                                                        });
                                                                        formRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Prescription Form Data</button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-success btn-sm mt-1 mb-1 me-1" 
                                                                    onClick={(e) => {
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            therapy_plan: item.appointment_prescription?.therapy_plan || '',
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            therapy_plan: '',
                                                                        });
                                                                        formMedicineRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Therapy Plan Data</button>
                                                            </td>
                                                        </tr>))}
                                                    </tbody>
                                                </table> : <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                                    Previous Appointments not found!
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12" ref={formPatientRef}>
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title">Prescription Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Patient ID:</strong> {appointmentPrescriptionData?.appointment?.patient_id}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Consultation ID:</strong> {appointmentPrescriptionData?.appointment?.id}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Consultation Date:</strong> {appointmentPrescriptionData?.appointment?.appointment_date_time}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Doctor Name:</strong> {appointmentPrescriptionData?.appointment?.doctor_name}
                                        </p>
                                    </div>
                                    <hr />
                                    <div className="col-md-6">
                                        <label className="form-label">Name</label>
                                        <div className="input-group mb-3">
                                            <select 
                                                className="form-select py-3 border-primary"
                                                name="namePrefix"
                                                value={prescriptionFormData?.patient_name_prefix}
                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name_prefix: e.target.value })}
                                                style={{ maxWidth: '120px' }}
                                            >
                                                <option value="">Choose...</option>
                                                {prefixOfName.map((prefix) => (
                                                    <option key={prefix} value={prefix}>{prefix}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="text"
                                                className="form-control py-3 border-primary"
                                                name="name"
                                                value={prescriptionFormData?.patient_name}
                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        {prescriptionFormError.patient_name && <div className="invalid-feedback d-block">Please enter a valid name.</div>}
                                        {prescriptionFormError.patient_name_prefix && <div className="invalid-feedback d-block">{prescriptionFormError.patient_name_prefix}</div>}
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">DOB</label>
                                        <input
                                            type="date"
                                            className="form-control py-3 border-primary"
                                            name="dob"
                                            value={prescriptionFormData?.patient_dob}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_dob: e.target.value })}
                                            required
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                        {prescriptionFormError.patient_dob && <div className="invalid-feedback d-block">Please enter a valid DOB.</div>}
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Gender</label>
                                        <select
                                            className="form-select py-3 border-primary"
                                            name="gender"
                                            value={prescriptionFormData?.patient_gender}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_gender: e.target.value })}
                                            required
                                        >
                                            <option value="">Choose...</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        {prescriptionFormError.patient_gender && <div className="invalid-feedback d-block">Please select a valid gender.</div>}
                                    </div>

                                    {/* Address Fields */}
                                    <div className="col-md-6">
                                        <label className="form-label">Address Line 1</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_line1"
                                            value={prescriptionFormData?.patient_address_line1}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line1: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_line1 && <div className="invalid-feedback d-block">Please enter a valid address.</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Address Line 2 (optional)</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_line2"
                                            value={prescriptionFormData?.patient_address_line2}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line2: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">District</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_district"
                                            value={prescriptionFormData?.patient_address_district}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_district: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_district && <div className="invalid-feedback d-block">Please enter a valid district.</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">State</label>
                                        <select
                                            className="form-select py-3 border-primary"
                                            name="address.state"
                                            value={prescriptionFormData?.patient_address_state}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_state: e.target.value })}
                                            required
                                        >
                                            <option value="">Choose...</option>
                                            {indStates.map((val) => (<option key={val.key} value={val.key}>{val.name}</option>))}
                                        </select>
                                        {prescriptionFormError.patient_address_state && <div className="invalid-feedback d-block">Please select a valid state.</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Pincode</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_pincode"
                                            value={prescriptionFormData?.patient_address_pincode}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_pincode: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_pincode && <div className="invalid-feedback d-block">Please enter a valid pincode (6-digits).</div>}
                                    </div>
                                    <hr />
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <b>Support & Helpline (10am-6pm):</b> +91 8240112175 <br /><b>Office Number:</b> +91 3482329183 
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12" ref={formRef}>
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title">Prescription Form</h5>
                            </div>
                            <div className="card-body">
                                <form>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="provisional_diagnosis" className="form-label">Provisional Diagnosis</label>
                                                <textarea className="form-control" id="provisional_diagnosis" rows="3" value={prescriptionFormData?.provisional_diagnosis} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, provisional_diagnosis: e.target.value })}></textarea>
                                                {prescriptionFormError?.provisional_diagnosis && <div className="text-danger">{prescriptionFormError?.provisional_diagnosis}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="present_concerns" className="form-label">Present Concerns</label>
                                                <textarea className="form-control" id="present_concerns" rows="3" value={prescriptionFormData?.present_concerns} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, present_concerns: e.target.value })}></textarea>
                                                {prescriptionFormError?.present_concerns && <div className="text-danger">{prescriptionFormError?.present_concerns}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="assessment_findings" className="form-label">Assessment Findings</label>
                                                <textarea className="form-control" id="assessment_findings" rows="3" value={prescriptionFormData?.assessment_findings} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, assessment_findings: e.target.value })}></textarea>
                                                {prescriptionFormError?.assessment_findings && <div className="text-danger">{prescriptionFormError?.assessment_findings}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="review_date" className="form-label">Review Date</label>
                                                <input className="form-control" id="review_date" type="date" value={prescriptionFormData?.review_date} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, review_date: e.target.value })} />
                                                {prescriptionFormError?.review_date && <div className="text-danger">{prescriptionFormError?.review_date}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="advice_home_program" className="form-label">Advice/Home Program</label>
                                                <textarea className="form-control" id="advice_home_program" rows="3" value={prescriptionFormData?.advice_home_program} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, advice_home_program: e.target.value })}></textarea>
                                                {prescriptionFormError?.advice_home_program && <div className="text-danger">{prescriptionFormError?.advice_home_program}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="recommendations" className="form-label">Recommendations</label>
                                                <textarea className="form-control" id="recommendations" rows="3" value={prescriptionFormData?.recommendations} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, recommendations: e.target.value })}></textarea>
                                                {prescriptionFormError?.recommendations && <div className="text-danger">{prescriptionFormError?.recommendations}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="frequency_of_therapy" className="form-label">Frequency of Therapy</label>
                                                <textarea className="form-control" id="frequency_of_therapy" rows="3" value={prescriptionFormData?.frequency_of_therapy} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, frequency_of_therapy: e.target.value })}></textarea>
                                                {prescriptionFormError?.frequency_of_therapy && <div className="text-danger">{prescriptionFormError?.frequency_of_therapy}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-12" ref={formMedicineRef}>
                                            <hr />
                                            <div className="mb-3">
                                                <label className="form-label">Therapy Plan</label>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered">
                                                        <thead>
                                                            <tr>
                                                                <th scope="col" style={{width:'70%'}}>Instructions</th>
                                                                <th scope="col">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {prescriptionFormData?.therapy_plan?.map((item, index) => (
                                                                <tr key={index}>
                                                                    <td style={{width:'70%'}}>
                                                                        <textarea className="form-control" id="plan_instruction" placeholder="Enter Instruction" rows="3" value={item.instruction} onChange={(e) => {
                                                                            const updatedTherapyPlan = prescriptionFormData?.therapy_plan?.map((planItem, planIndex) => planIndex === index ? { ...planItem, instruction: e.target.value } : planItem) || [];
                                                                            setPrescriptionFormData({ ...prescriptionFormData, therapy_plan: updatedTherapyPlan });
                                                                        }}></textarea>
                                                                    </td>
                                                                    <td>
                                                                        <button type="button" className="btn btn-danger btn-sm mt-1 mb-1 me-1" onClick={() => { removePlanFormData(index) }}>Remove</button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr>
                                                                <td style={{width:'70%'}}>
                                                                    {prescriptionFormError?.therapy_plan && <div className="text-danger">{prescriptionFormError?.therapy_plan}</div>}
                                                                </td>
                                                                <td>
                                                                    <button type="button" className="btn btn-primary" onClick={addPlanFormData}>Add</button>
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                            <hr />
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="remarks" className="form-label">REMARKS</label>
                                                <textarea className="form-control" id="remarks" rows="3" value={prescriptionFormData?.remarks} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, remarks: e.target.value })}></textarea>
                                                {prescriptionFormError?.remarks && <div className="text-danger">{prescriptionFormError?.remarks}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="remarks" className="form-label">Appointment Completed</label>
                                                <div className="input-group mb-3">
                                                    <select 
                                                        className="form-select border-primary" 
                                                        name="isCompleted" 
                                                        value={prescriptionFormData?.is_completed}
                                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, is_completed: e.target.value })}
                                                    >
                                                        {['Yes','No'].map((item) => (
                                                            <option key={item} value={item}>{item}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {prescriptionFormError?.is_completed && <div className="text-danger">{prescriptionFormError?.is_completed}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <button type="button" className="btn btn-primary float-end mt-5" onClick={handlePrescription}>Submit</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}